import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { SiteShell } from "@/components/layout/SiteShell";
import { Faq } from "@/components/Faq";
import { track } from "@/lib/analytics";
import { GENERIC_BOKADIREKT } from "@/lib/booking";
import { jsonLd, SITE_URL, absUrl, faqSchema, breadcrumbSchema } from "@/lib/seo";
import herrklippningAsset from "@/assets/opt/herrklippning-tailors-barber.webp";
import taperFadeAsset from "@/assets/opt/taper-fade-tailors-barber.webp";
import skaggAsset from "@/assets/opt/skagg-tailors-barber.webp";
import klippningSkaggAsset from "@/assets/opt/klippning-skagg-tailors-barber.webp";
const CANONICAL = `${SITE_URL}/vilken-frisyr-passar-dig`;
const TITLE = "Vilken frisyr passar dig? Guide + quiz | Tailor's Barber";
const DESCRIPTION =
  "Ta reda på vilken frisyr som passar din ansiktsform och hårtyp. Guide till oval, rund, fyrkantig och hjärtformad ansiktsform + snabbt quiz som rekommenderar rätt klippning.";

const FACE_SHAPES = [
  {
    key: "oval",
    name: "Oval",
    desc: "Balanserade proportioner – nästan alla frisyrer fungerar. Undvik att täcka pannan helt.",
    styles: ["Textured crop", "Slick back", "Klassisk sidbena", "Modern pompadour"],
    image: herrklippningAsset,
  },
  {
    key: "rund",
    name: "Rund",
    desc: "Skapa höjd på toppen och håll sidorna korta för att förlänga ansiktet.",
    styles: ["Taper fade med volym på toppen", "Pompadour", "Quiff", "Kort sidbena"],
    image: taperFadeAsset,
  },
  {
    key: "fyrkantig",
    name: "Fyrkantig",
    desc: "Stark käklinje – välj mjukare texturer och undvik för raka linjer.",
    styles: ["Textured crop", "Messy fringe", "Mjuk sidbena", "Buzz cut"],
    image: klippningSkaggAsset,
  },
  {
    key: "hjart",
    name: "Hjärtformad",
    desc: "Bred panna, smalare haka. Undvik för mycket volym på toppen – bygg vid sidorna.",
    styles: ["Side part", "Medium length swept back", "Fringe / lugg", "Skägg för att balansera hakan"],
    image: skaggAsset,
  },
  {
    key: "avlang",
    name: "Avlång",
    desc: "Undvik höjd på toppen. Håll det medium på sidorna för att bredda ansiktet.",
    styles: ["Fringe", "Crop med lugg", "Medium sidbena", "Fylligare skägg"],
    image: herrklippningAsset,
  },
  {
    key: "tunnhar",
    name: "Tunnhårig",
    desc: "Håret tunnar av på toppen eller vid tinningarna. Kortare och texturerade klippningar döljer glesare partier bäst – undvik långa klippningar och inåtstrykningar.",
    styles: [
      "Textured crop med fade",
      "French crop med lugg",
      "Buzz cut med taper",
      "Kort och texturerat med hög fade",
      "Skin fade mot buzz",
    ],
    image: taperFadeAsset,
  },
] as const;

type ShapeKey = (typeof FACE_SHAPES)[number]["key"];

const QUIZ = [
  {
    q: "Hur skulle du beskriva formen på ditt ansikte?",
    options: [
      { label: "Ungefär lika brett som långt, mjuka linjer", shape: "rund" as ShapeKey },
      { label: "Längre än det är brett, jämna proportioner", shape: "oval" as ShapeKey },
      { label: "Bred panna, smalare haka", shape: "hjart" as ShapeKey },
      { label: "Markerad, kantig käklinje", shape: "fyrkantig" as ShapeKey },
      { label: "Tydligt avlångt", shape: "avlang" as ShapeKey },
      { label: "Tunnhårig – håret tunnar av på toppen eller tinningarna", shape: "tunnhar" as ShapeKey },
    ],
  },
  {
    q: "Vilken hårtyp har du?",
    options: [
      { label: "Rakt och fint", tag: "rakt" },
      { label: "Tjockt och rakt", tag: "tjockt" },
      { label: "Vågigt", tag: "vagigt" },
      { label: "Krulligt", tag: "lockigt" },
    ],
  },
  {
    q: "Hur mycket underhåll är du okej med?",
    options: [
      { label: "Så lite som möjligt", tag: "lag" },
      { label: "Lite styling på morgonen", tag: "medel" },
      { label: "Jag gillar att styla varje dag", tag: "hog" },
    ],
  },
  {
    q: "Vilken känsla är du ute efter?",
    options: [
      { label: "Klassisk och stilren", tag: "klassisk" },
      { label: "Modern och skarp", tag: "modern" },
      { label: "Avslappnad och texturerad", tag: "textur" },
    ],
  },
] as const;

const FAQ = [
  {
    q: "Hur vet jag vilken ansiktsform jag har?",
    a: "Kamma bort håret från pannan, ställ dig framför spegeln och titta på proportionerna mellan panna, kindben och haka. Är ansiktet ungefär lika brett som långt är det runt; är det längre än brett med jämna kurvor är det ovalt; en markerad käklinje pekar mot fyrkantig; bred panna och smal haka mot hjärtformad. Osäker? Din barberare bedömer det på sekunder.",
  },
  {
    q: "Vilken frisyr passar alla ansiktsformen?",
    a: "En textured crop med taper fade är den mest allsidiga moderna klippningen och fungerar för de flesta ansiktsformer och hårtyper. Volymen på toppen kan justeras för att balansera dina proportioner.",
  },
  {
    q: "Räknas hårtyp mer än ansiktsform?",
    a: "Båda spelar roll. Ansiktsformen avgör siluetten som klär dig; hårtypen avgör vilka klippningar som faktiskt går att styla i vardagen. En bra barberare väger in båda.",
  },
  {
    q: "Kan ni hjälpa mig välja på plats?",
    a: "Absolut. Alla våra barberare gör en konsultation innan klippningen där vi går igenom ansiktsform, hårtyp, livsstil och hur mycket tid du vill lägga på styling. Boka en herrklippning så tar vi det därifrån.",
  },
];

function Quiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [shape, setShape] = useState<ShapeKey | null>(null);

  const result = useMemo(() => {
    if (!shape) return null;
    return FACE_SHAPES.find((f) => f.key === shape) ?? null;
  }, [shape]);

  function choose(idx: number, value: string, shapeVal?: ShapeKey) {
    const next = { ...answers, [idx]: value };
    setAnswers(next);
    if (idx === 0 && shapeVal) setShape(shapeVal);
    if (idx < QUIZ.length - 1) {
      setStep(idx + 1);
    } else {
      // quiz completed
    }
  }

  function reset() {
    setStep(0);
    setAnswers({});
    setShape(null);
  }

  const done = Object.keys(answers).length === QUIZ.length;

  if (done && result) {
    return (
      <div className="card-elev p-6 md:p-10">
        <div className="eyebrow">Din rekommendation</div>
        <h3 className="mt-2 text-3xl md:text-4xl">
          {result.key === "tunnhar" ? "Hårtyp" : "Ansiktsform"}: {result.name}
        </h3>
        <p className="mt-3 text-foreground/75 max-w-prose">{result.desc}</p>
        <div className="mt-6 grid gap-6 md:grid-cols-[2fr_3fr] items-start">
          <div className="aspect-square rounded-xl overflow-hidden bg-muted">
            <img src={result.image} alt={result.name} className="h-full w-full object-cover" />
          </div>
          <div>
            <div className="eyebrow">Frisyrer som passar</div>
            <ul className="mt-2 space-y-2">
              {result.styles.map((s) => (
                <li key={s} className="flex gap-2">
                  <span className="text-brass-deep">→</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={GENERIC_BOKADIREKT}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  track("book_now_click", { cta_location: "quiz_result" })
                }
                className="btn-brass"
              >
                Boka konsultation & klippning
              </a>
              <button type="button" onClick={reset} className="btn-outline">
                Gör om quizet
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const current = QUIZ[step];
  return (
    <div className="card-elev p-6 md:p-10">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>Fråga {step + 1} av {QUIZ.length}</span>
        <div className="flex gap-1">
          {QUIZ.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 w-6 rounded-full ${i <= step ? "bg-brass-deep" : "bg-muted"}`}
            />
          ))}
        </div>
      </div>
      <h3 className="mt-4 text-2xl md:text-3xl">{current.q}</h3>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {current.options.map((opt) => (
          <button
            key={opt.label}
            type="button"
            onClick={() =>
              choose(
                step,
                opt.label,
                "shape" in opt ? (opt.shape as ShapeKey) : undefined,
              )
            }
            className="text-left px-5 py-4 rounded-xl border border-border hover:border-brass-deep hover:bg-muted/40 transition-colors"
          >
            {opt.label}
          </button>
        ))}
      </div>
      {step > 0 && (
        <button
          type="button"
          onClick={() => setStep(step - 1)}
          className="mt-6 text-sm text-muted-foreground hover:text-foreground"
        >
          ← Föregående
        </button>
      )}
    </div>
  );
}

function Page() {
  return (
    <SiteShell>
      <section className="container-page pt-10 md:pt-16 pb-8">
        <nav className="text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:underline">Hem</Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Vilken frisyr passar dig?</span>
        </nav>
        <div className="max-w-3xl">
          <div className="eyebrow">Guide & quiz</div>
          <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            Vilken frisyr passar dig?
          </h1>
          <p className="mt-5 text-lg text-foreground/75">
            Rätt frisyr handlar om tre saker: din ansiktsform, din hårtyp och hur mycket tid du vill
            lägga på styling. Här går vi igenom de fem vanligaste ansiktsformerna och vilka klippningar
            som klär dem – och längst ner gör du ett snabbt quiz som ger dig en personlig rekommendation.
          </p>
        </div>
      </section>

      <section className="container-page py-10 md:py-16">
        <div className="max-w-2xl mb-8">
          <div className="eyebrow">Ansiktsformer</div>
          <h2 className="mt-3 text-3xl md:text-4xl">Hitta din form</h2>
          <p className="mt-3 text-foreground/70">
            Kamma bort håret från pannan och jämför proportionerna mellan panna, kindben och haka.
          </p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FACE_SHAPES.map((f) => (
            <article key={f.key} className="card-elev overflow-hidden">
              <div className="aspect-[4/3] bg-muted">
                <img src={f.image} alt={`Frisyr för ${f.name.toLowerCase()} ansiktsform`} className="h-full w-full object-cover" loading="lazy" />
              </div>
              <div className="p-6">
                <h3 className="text-xl">{f.name}</h3>
                <p className="mt-2 text-sm text-foreground/75">{f.desc}</p>
                <ul className="mt-3 space-y-1 text-sm text-foreground/80">
                  {f.styles.map((s) => (
                    <li key={s}>• {s}</li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="container-page py-12 md:py-20">
        <div className="max-w-2xl mb-8">
          <div className="eyebrow">Personlig rekommendation</div>
          <h2 className="mt-3 text-3xl md:text-4xl">Gör quizet</h2>
          <p className="mt-3 text-foreground/70">
            Fyra snabba frågor. Vi föreslår en frisyr som matchar din form, hårtyp och vardag.
          </p>
        </div>
        <Quiz />
      </section>

      <section className="container-page py-12">
        <div className="grid gap-6 md:grid-cols-3">
          <Link to="/herrklippning/" className="card-elev p-6 hover:border-brass-deep transition-colors">
            <div className="eyebrow">Boka</div>
            <h3 className="mt-2 text-xl">Herrklippning</h3>
            <p className="text-sm text-muted-foreground mt-1">Klassisk klippning med konsultation.</p>
          </Link>
          <Link to="/taper-fade/" className="card-elev p-6 hover:border-brass-deep transition-colors">
            <div className="eyebrow">Boka</div>
            <h3 className="mt-2 text-xl">Taper Fade</h3>
            <p className="text-sm text-muted-foreground mt-1">Mjuk övergång – funkar för de flesta.</p>
          </Link>
          <Link to="/herrklippning-skaggtrimning/" className="card-elev p-6 hover:border-brass-deep transition-colors">
            <div className="eyebrow">Boka</div>
            <h3 className="mt-2 text-xl">Klippning & Skägg</h3>
            <p className="text-sm text-muted-foreground mt-1">Komplett look – hår och skägg i ett pass.</p>
          </Link>
        </div>
      </section>

      <Faq items={FAQ} heading="Vanliga frågor om att välja frisyr" />
    </SiteShell>
  );
}

export const Route = createFileRoute("/vilken-frisyr-passar-dig")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "robots", content: "noindex, follow" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absUrl(CANONICAL) },
      { property: "og:image", content: absUrl(herrklippningAsset) },
    ],
    links: [{ rel: "canonical", href: absUrl(CANONICAL) }],
    scripts: [
      jsonLd(faqSchema(FAQ)),
      jsonLd(
        breadcrumbSchema([
          { name: "Hem", url: "/" },
          { name: "Vilken frisyr passar dig?", url: "/vilken-frisyr-passar-dig" },
        ]),
      ),
    ],
  }),
  component: Page,
});
